import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExcelUpload from "./ExcelUpload";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import Select from "react-select";
import { PulseLoader } from "react-spinners";
import { Plus, X, Trash2 } from "lucide-react";

const AddInventoryModal = ({ onClose, onAddInventory }) => {
  const [formData, setFormData] = useState([
    {
      itemCode: "",
      itemName: "",
      itemCategory: "",
      itemQtyCtn: "",
      openingStock: "",
      warehouseName: "",
      itemImage: "",
      inwardReference: "",
      inwardShipmentMark: "",
    },
  ]);
  const [warehouses, setWarehouses] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectStyle = {
    menuPortal: (base) => ({
      ...base,
      width: "20rem",
      zIndex: 9999,
    }),
    control: (provided) => ({
      ...provided,
      minHeight: "30px",
      height: "30px",
      borderRadius: "6px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "30px",
      padding: "0 4px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "30px",
    }),
  };

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          buildApiUrl("/api/warehouse/get-warehouse/name"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        if (response.ok) {
          setWarehouses(result.data);
        } else {
          toast.error(result.message || "Failed to fetch warehouses");
        }
      } catch (error) {
        toast.error("An error occurred while fetching warehouse names");
      }
    };

    const fetchItemCodes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          buildApiUrl("/api/categories"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        if (response.ok) {
          const options = result.map((item) => ({
            value: item.itemCode,
            label: item.itemCode,
          }));
          setItemOptions(options);
        } else {
          toast.error(result.message || "Failed to fetch item codes");
        }
      } catch (error) {
        toast.error("An error occurred while fetching item codes");
      }
    };

    fetchWarehouses();
    fetchItemCodes();
  }, []);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const newFormData = [...formData];
    newFormData[index][name] = value;
    setFormData(newFormData);
  };

  const handleItemCodeChange = async (index, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    const newFormData = [...formData];
    newFormData[index].itemCode = value;
    setFormData(newFormData);

    if (!value) {
      newFormData[index].itemName = "";
      newFormData[index].itemCategory = "";
      newFormData[index].itemImage = "";
      newFormData[index].itemQtyCtn = "";
      setFormData(newFormData);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(`/api/categories/itemcode/${value}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        const updatedFormData = [...formData];
        updatedFormData[index] = {
          ...updatedFormData[index],
          itemCode: result.itemCode,
          itemName: result.itemName,
          itemCategory: result.categoryName,
          itemImage: result.itemImage,
          itemQtyCtn: result.pcsPerCtn || "",
        };
        setFormData(updatedFormData);
      } else {
        const updatedFormData = [...formData];
        updatedFormData[index] = {
          ...updatedFormData[index],
          itemName: "",
          itemCategory: "",
          itemImage: "",
          itemQtyCtn: "",
        };
        setFormData(updatedFormData);
        toast.error(result.message || "Item not found");
      }
    } catch (error) {
      const updatedFormData = [...formData];
      updatedFormData[index] = {
        ...updatedFormData[index],
        itemName: "",
        itemCategory: "",
        itemImage: "",
        itemQtyCtn: "",
      };
      setFormData(updatedFormData);
      toast.error("An error occurred while fetching item details");
    }
  };

  const handleDelete = (index) => {
    if (formData.length === 1) {
      toast.error("Cannot delete the last item");
      return;
    }
    const newFormData = formData.filter((_, i) => i !== index);
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const BATCH_SIZE = 100;
      const batches = [];
      for (let i = 0; i < formData.length; i += BATCH_SIZE) {
        batches.push(formData.slice(i, i + BATCH_SIZE));
      }

      let totalProcessed = 0;
      for (let batch of batches) {
        const response = await fetch(
          buildApiUrl("/api/warehouse/inventory"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ items: batch }),
          }
        );

        const result = await response.json();
        if (response.ok) {
          totalProcessed += batch.length;
          setProgress((totalProcessed / formData.length) * 100);
          if (result.data && Array.isArray(result.data)) {
            onAddInventory(result.data);
          }
        } else {
          toast.error(result.message || "Failed to add inventory items");
          break;
        }
      }
      toast.success("Inventory added successfully!");
      onClose();
    } catch (error) {
      toast.error("An error occurred while adding inventory items");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNewInventory = () => {
    setFormData([
      ...formData,
      {
        itemCode: "",
        itemName: "",
        itemCategory: "",
        itemQtyCtn: "",
        openingStock: "",
        warehouseName: "",
        itemImage: "",
        inwardReference: "",
        inwardShipmentMark: "",
      },
    ]);
  };

  const fillFormFromExcel = (data) => {
    const newFormData = data.map((item) => ({
      itemCode: item.itemCode,
      itemName: item.itemName,
      itemCategory: item.itemCategory,
      itemQtyCtn: item.itemQtyCtn,
      openingStock: item.openingStock || "",
      warehouseName: item.warehouseName.toLowerCase(),
      itemImage: item.itemImage,
      inwardReference: item.inwardReference,
      inwardShipmentMark: item.inwardShipmentMark,
    }));
    setFormData(newFormData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div
        className="w-full max-w-5xl rounded-lg bg-white shadow-xl overflow-y-auto"
        style={{ maxHeight: "90vh", scrollbarWidth: "thin" }}
      >
        <div className="flex items-center justify-between px-4 pt-2">
          <h2 className="text-lg font-semibold text-gray-900">Add Inventory</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-2 py-1">
          <div className="mb-2 flex flex-col sm:flex-row sm:items-end gap-4 border border-gray-300 w-fit px-2 py-1 rounded-md bg-gray-50">
            <ExcelUpload onFillForm={fillFormFromExcel} />
          </div>

          {isSubmitting && (
            <div className="flex justify-center items-center gap-3 mb-4 text-gray-600">
              <PulseLoader color="#4caf50" size={10} />
              <span>Uploading Inventory...</span>
            </div>
          )}

          <div className="mb-4">
            <div className="hidden sm:grid grid-cols-[2fr_2fr_1fr_2fr_1fr_2fr_1fr_1fr_1fr_20px] gap-2 text-xs  tracking-wide py-2 text-gray-700 font-medium tracking-gray-700 rounded-md mb-2 items-center bg-gray-200">
              <span className="pl-2">Warehouse Name</span>
              <span>Item Code</span>
              <span>Category</span>
              <span>Item Name</span>
              <span>Qty/Ctn</span>
              <span title="Item Image URL">Img URL</span>
              <span title="Opening Stock">O. Stock</span>
              <span title="Inward Reference">In. Ref.</span>
              <span title="Shipment Mark">Ship. Mark</span>
              <span></span>
            </div>

            <form onSubmit={handleSubmit}>
              {formData.map((inventory, index) => (
                <div
                  key={index}
                  className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 sm:p-0 sm:border-0"
                >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[2fr_2fr_1fr_2fr_1fr_2fr_1fr_1fr_1fr_20px] items-center">
                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Warehouse Name
                      </label>
                      <select
                        name="warehouseName"
                        value={inventory.warehouseName}
                        onChange={(e) => handleChange(index, e)}
                        required
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs sm:text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="" disabled>
                          Select a warehouse
                        </option>
                        {warehouses.map((warehouse) => (
                          <option
                            key={warehouse._id}
                            value={warehouse.warehouseName}
                          >
                            {warehouse.warehouseName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Item Code
                      </label>
                      <Select
                        options={itemOptions}
                        value={
                          itemOptions.find(
                            (option) => option.value === inventory.itemCode
                          ) || null
                        }
                        onChange={(selected) =>
                          handleItemCodeChange(index, selected)
                        }
                        styles={selectStyle}
                        menuPortalTarget={document.body}
                        placeholder="Select item code"
                        className="text-xs sm:text-xs"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Category
                      </label>
                      <input
                        readOnly
                        type="text"
                        name="itemCategory"
                        value={inventory.itemCategory}
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 bg-gray-200 text-xs sm:text-xs"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Item Name
                      </label>
                      <input
                        readOnly
                        type="text"
                        name="itemName"
                        value={inventory.itemName}
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 bg-gray-200 text-xs sm:text-xs"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Qty/Ctn
                      </label>
                      <input
                        readOnly
                        type="number"
                        name="itemQtyCtn"
                        value={inventory.itemQtyCtn}
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 bg-gray-200 text-xs sm:text-xs"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Item Image URL
                      </label>
                      <input
                        readOnly
                        type="url"
                        name="itemImage"
                        value={inventory.itemImage}
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 bg-gray-200 text-xs sm:text-xs"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Opening Stock
                      </label>
                      <input
                        type="number"
                        name="openingStock"
                        value={inventory.openingStock}
                        onChange={(e) => handleChange(index, e)}
                        required
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 text-xs sm:text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Inward Reference
                      </label>
                      <input
                        type="text"
                        name="inwardReference"
                        value={inventory.inwardReference}
                        onChange={(e) => handleChange(index, e)}
                        required
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 text-xs sm:text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                        Shipment Mark
                      </label>
                      <input
                        type="text"
                        name="inwardShipmentMark"
                        value={inventory.inwardShipmentMark}
                        onChange={(e) => handleChange(index, e)}
                        required
                        className="w-full px-2 py-1.5 rounded-md border border-gray-300 text-xs sm:text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex justify-center justify-center">
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="text-red-500 hover:text-red-70"
                        title="Delete Item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-between mt-4">
                <button
                  type="button"
                  onClick={addNewInventory}
                  className="flex items-center justify-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-xs text-white hover:bg-blue-700"
                >
                  <Plus size={16} />
                  Add Inventory
                </button>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={onClose}
                    className="rounded-md bg-gray-200 px-4 py-2 text-xs text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-xs text-white hover:bg-indigo-700 disabled:bg-indigo-400"
                  >
                    {isSubmitting ? "Processing..." : "Save Inventory"}
                  </button>
                </div>
              </div>
            </form>

            {isSubmitting && (
              <div className="absolute inset-0 flex justify-center items-center z-100 bg-black bg-opacity-50 rounded-lg">
                <div className="flex flex-col justify-center items-center space-y-3">
                  <div
                    style={{
                      width: "100px",
                      height: "100px",
                      transition: "transform 0.3s ease-in-out",
                    }}
                  >
                    <CircularProgressbar
                      value={progress}
                      progress={progress}
                      text={`${Math.round(progress)}%`}
                      styles={{
                        path: {
                          stroke: "#4CAF50",
                          strokeLinecap: "round",
                          transition: "stroke-dashoffset 0.5s ease 0s",
                        },
                        trail: {
                          stroke: "#e0e0e0",
                        },
                        text: {
                          fill: "#4CAF50",
                          fontSize: "16px",
                          fontWeight: "bold",
                        },
                      }}
                    />
                  </div>
                  <span className="text-white text-xs text-semibold font-bold">
                    Uploading Inventory...
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <ToastContainer />
      </div>
    </div>
  );
};

export default AddInventoryModal;
